import { z } from "zod";
import {
  EventIdPublicSchema,
  EventParticipantIdPublicSchema,
} from "./branded-id.schemas.js";

export const AdminNetworkSearchQuerySchema = z.object({
  query: z.string().trim().min(1),
  eventIdPublic: EventIdPublicSchema.optional(),
  // An enum, not `z.coerce.boolean()`, which reads `?deep=false` as true.
  deep: z
    .enum(["true", "false"])
    .optional()
    .default("false")
    .transform((value) => value === "true"),
});

export type AdminNetworkSearchQuery = z.infer<
  typeof AdminNetworkSearchQuerySchema
>;

/**
 * The participants whose engine reasons best answer a free-text ask
 * (`GET /api/v1/admin/network/search`), within the event `eventIdPublic` names,
 * or across every event when it is absent; each match says which event it is in.
 * Only open reasons are searched unless `deep`, which searches every reason.
 *
 * `ask` is the query read as a brief reason: `interpretation` restates it as a
 * full sentence, which is what the search embeds. One entry per person — participants
 * sharing a user count once — carrying their best-scoring reason across events: `score` is `similarity` (cosine with the brief's
 * noise floor taken out of both sides) with direction fit and confidence
 * applied. The highest-scoring people are graded against the ask (`fit`, with
 * the judge's `confidence`, 0 to 1); only direct answers are `results`, ordered by
 * source — stated, then researched (registration, LinkedIn, host input), then
 * inferred — then score. At most five, possibly none. Candidates the judge
 * turned down are not sent: a guarded reason must not leave the server unless
 * it answers the ask.
 */
const NetworkSearchMatchSchema = z.object({
  participantIdPublic: EventParticipantIdPublicSchema,
  eventIdPublic: EventIdPublicSchema,
  fullName: z.string(),
  fit: z.enum(["direct", "partial", "none"]),
  confidence: z.number(),
  score: z.number(),
  similarity: z.number(),
  reason: z.object({
    type: z.string(),
    direction: z.string(),
    confidence: z.string(),
    source: z.string(),
    signal: z.string(),
  }),
});

export const AdminNetworkSearchResultSchema = z.object({
  ask: z.object({
    interpretation: z.string(),
    type: z.string(),
    direction: z.string(),
    confidence: z.string(),
  }),
  results: z.array(NetworkSearchMatchSchema.extend({ rank: z.number().int() })),
});

export type AdminNetworkSearchResult = z.infer<
  typeof AdminNetworkSearchResultSchema
>;
